<template>
  <li class="section-item-accordion" :class="{ 'section-item--open': expanded }">
    <div
      class="section-item-accordion__header d-flex align-items-center gap-1"
      role="button"
      :aria-expanded="expanded"
      @click="emit('toggle')"
    >
      <span v-if="favorite" class="section-item-accordion__favorite-pin flex-shrink-0">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="12" y1="17" x2="12" y2="22" />
          <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
        </svg>
      </span>
      <p class="section-item-accordion__title text-truncate mb-0">{{ title }}</p>
      <div
        v-if="pinnable"
        ref="menuRef"
        class="section-item-accordion__menu flex-shrink-0"
        :class="{ 'section-item-accordion__menu--open': isMenuOpen }"
        @click.stop
      >
        <IconButton
          size="small"
          icon="fa fa-ellipsis-h"
          aria-label="Section options"
          :class="{ 'section-item-accordion__menu-trigger--active': isMenuOpen }"
          @click="toggleMenu"
        />
      </div>
      <Teleport to="body">
        <div v-if="isMenuOpen" ref="dropdownRef" class="section-item-accordion__dropdown" :style="dropdownStyle">
          <button class="section-item-accordion__dropdown-item" @click="onToggleFavorite">
            <svg
              v-if="!favorite"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="section-item-accordion__dropdown-icon"
            >
              <line x1="12" y1="17" x2="12" y2="22" />
              <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="section-item-accordion__dropdown-icon"
            >
              <line x1="2" y1="2" x2="22" y2="22" />
              <line x1="12" y1="17" x2="12" y2="22" />
              <path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h12.76" />
              <path d="M19 17.76V15.24a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8" />
            </svg>
            {{ favorite ? 'Unpin section' : 'Pin section' }}
          </button>
          <button v-if="favorite" class="section-item-accordion__dropdown-item" @click="onOpenArrange">
            <i class="fa fa-sliders"></i>
            Arrange pinned sections
          </button>
        </div>
      </Teleport>
      <span
        class="section-item-accordion__chevron d-inline-flex align-items-center justify-content-center flex-shrink-0"
        :class="{ 'section-item-accordion__chevron--open': expanded }"
      >
        <i class="fa fa-angle-right"></i>
      </span>
    </div>

    <ul v-if="expanded">
      <slot />
    </ul>
  </li>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, onUnmounted, nextTick, type Ref } from 'vue';
import IconButton from '@/components/common/IconButton.vue';

defineOptions({ name: 'SectionItem' });

withDefaults(
  defineProps<{
    title: string;
    expanded?: boolean;
    favorite?: boolean;
    pinnable?: boolean;
  }>(),
  {
    expanded: false,
    favorite: false,
    pinnable: true,
  }
);

const emit = defineEmits<{
  toggle: [];
  toggleFavorite: [];
  openArrange: [];
}>();

const menuRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const dropdownStyle = ref({ top: '0px', right: '0px' });
const menuId = Math.random().toString(36).slice(2, 11);
const openMenuId = inject<Ref<string | null>>('openMenuId', ref(null));
const isMenuOpen = computed(() => openMenuId.value === menuId);

function closeMenu() {
  if (openMenuId.value === menuId) {
    openMenuId.value = null;
  }
}

function updateDropdownPosition() {
  if (!menuRef.value) return;
  const rect = menuRef.value.getBoundingClientRect();
  dropdownStyle.value = {
    top: `${rect.bottom + 4}px`,
    right: `${window.innerWidth - rect.right}px`,
  };
}

function toggleMenu() {
  if (isMenuOpen.value) {
    closeMenu();
  } else {
    openMenuId.value = menuId;
    nextTick(updateDropdownPosition);
  }
}

function onToggleFavorite() {
  closeMenu();
  emit('toggleFavorite');
}

function onOpenArrange() {
  closeMenu();
  emit('openArrange');
}

function handleClickOutside(event: MouseEvent) {
  if (!isMenuOpen.value) return;
  const target = event.target as Node;
  const clickedMenu = menuRef.value?.contains(target);
  const clickedDropdown = dropdownRef.value?.contains(target);
  if (!clickedMenu && !clickedDropdown) {
    closeMenu();
  }
}

function handleScroll() {
  if (isMenuOpen.value) closeMenu();
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('scroll', handleScroll, true);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('scroll', handleScroll, true);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utils/variables";

.section-item-accordion {
  :deep(.sidebar-link.active) {
    background-color: transparent !important;
    margin-bottom: 0 !important;
  }
}

.section-item-accordion__header {
  cursor: pointer;
  padding: 12px 12px 12px 18px;
  position: relative;


  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: rgba($badge-light-color, 0.1);
  }

  &:hover {
    background: rgba($badge-light-color, 0.08);
    box-shadow: 0 6px 18px rgba($badge-light-color, 0.1);
  }

  &:active {
    transform: translateY(1px);
  }
}

.section-item-accordion.section-item--open {
  border-right: 1px solid rgba($primary-color, 0.2);

  .section-item-accordion__header {
    background: rgba($primary-color, 0.1);
    box-shadow: 0 8px 22px rgba($badge-light-color, 0.12);
  }

  > ul {
    background: rgba($badge-light-color, 0.04);
  }
}

.section-item-accordion__favorite-pin {
  width: 12px;
  height: 12px;
  color: $warning-color;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
}

.section-item-accordion__title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #52526c;
}

.section-item-accordion__menu {
  position: relative;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  transition:
    max-width 0.15s ease,
    opacity 0.15s ease;
}

.section-item-accordion:hover .section-item-accordion__menu,
.section-item-accordion__menu--open {
  max-width: 28px;
  overflow: visible;
  opacity: 1;
}

.section-item-accordion__menu-trigger--active {
  background-color: rgba($badge-light-color, 0.15) !important;
  color: $theme-body-sub-title-color !important;
}

.section-item-accordion__dropdown {
  position: fixed;
  z-index: 1001;
  background: #ffffff;
  border: 1px solid rgba($badge-light-color, 0.15);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba($badge-light-color, 0.15);
  min-width: 190px;
  padding: 4px 0;
}

.section-item-accordion__dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  color: $theme-body-font-color;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;

  i {
    width: 14px;
    text-align: center;
    color: $font-gray-color;
    flex-shrink: 0;
  }

  &:hover {
    background-color: rgba($badge-light-color, 0.08);
  }
}

.section-item-accordion__dropdown-icon {
  width: 14px;
  height: 14px;
  color: $font-gray-color;
  flex-shrink: 0;
}

.section-item-accordion__chevron {
  width: 12px;
  height: 12px;
  color: $badge-light-color;
  transition:
    background-color 0.18s ease,
    color 0.18s ease,
    transform 0.2s ease;

  &--open {
    transform: rotate(90deg);
  }
}
</style>
